import { createModel } from "@rematch/core";
import { api } from "api";
import {
  ApiRolePermissionsUpdateRolePermissionRelationsPutRequest,
  GetRolePermissionRelationsQuery,
  PermissionVm,
  PermissionVmPaginatedSearchResponse,
  RoleListVm,
  RolePermissionDto,
} from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  mergeListParams,
  mergeListParamsWithPagination,
  OrderByType,
} from "widgets/table/useTableUtils";

export type GetPermissionRoleRelationsRequestParams =
  ListRequestParams<GetRolePermissionRelationsQuery>;

interface PermissionsAndRolesAssignState {
  relations: RolePermissionDto[] | null | undefined;
  listParams: GetPermissionRoleRelationsRequestParams;
  permissions?: PermissionVm[] | null;
  roles?: RoleListVm[] | null;
  filterRoleId?: string | null;
  allPermissions: PermissionVm[];
  isSaveDisabled: boolean;
}

const initialState: PermissionsAndRolesAssignState = {
  roles: [],
  permissions: [],
  relations: [],
  listParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "name",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  filterRoleId: "",
  allPermissions: [],
  isSaveDisabled: true,
};

export const permissionsAndRolesAssign = createModel<RootModel>()({
  state: initialState,
  reducers: {
    resetListParams(state) {
      state.listParams = {
        ...initialState.listParams,
        order: initialState.listParams.order,
      };
      state.filterRoleId = "";
    },
    setPermissionRoleRelations(
      state,
      payload: RolePermissionDto[] | null | undefined
    ) {
      state.relations = payload;
    },
    setRoles(state, payload: RoleListVm[] | undefined | null) {
      state.roles = payload;
    },
    setFilterRoleId(state, payload: string | null) {
      state.filterRoleId = payload;
    },
    setPermissionsList(
      state,
      {
        data,
        listParams,
      }: {
        data: PermissionVm[];
        listParams: GetPermissionRoleRelationsRequestParams;
      }
    ) {
      state.permissions = data;
      state.listParams = listParams;
    },
    setAllPermsissions(state, payload: PermissionVm[]) {
      state.allPermissions = payload;
    },
    setIsSaveDisabled(state, payload: boolean) {
      state.isSaveDisabled = payload;
    },
    reset: () => initialState,
  },

  effects: (dispatch) => {
    const { permissionsAndRolesAssign } = dispatch;

    return {
      async getPermissionRoleRelations(
        params: GetPermissionRoleRelationsRequestParams = {},
        state
      ): Promise<void> {
        const mergedParams = mergeListParams(
          state.permissionsAndRolesAssign.listParams,
          {
            ...params,
            filter: {
              ...params.filter,
              roleId: state.permissionsAndRolesAssign.filterRoleId,
            },
            order: [
              {
                orderBy: "name",
                orderByType: OrderByType.Ascending,
              },
            ],
          }
        );

        const {
          permissions: permissionIds,
          roleId,
          ...filters
        } = mergedParams.filter ?? {};

        const getAllQuery = mapToGetAllQuery({
          ...mergedParams,
          filter: filters,
        });
        const data =
          await api.lumen.rolePermissions.apiRolePermissionsGetRolePermissionRelationsPost(
            {
              getRolePermissionRelationsQuery: {
                ...getAllQuery,
                roleId,
                permissions: permissionIds,
              },
            }
          );

        const { permissions, roles, relations } = data;

        dispatch(
          permissionsAndRolesAssign.setPermissionRoleRelations(relations)
        );
        dispatch(permissionsAndRolesAssign.setRoles(roles));

        const { items, ...pagination } =
          permissions as PermissionVmPaginatedSearchResponse;

        dispatch(
          permissionsAndRolesAssign.setPermissionsList({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async updatePermissionRoleRelations(
        updateRolePermissionRelationsCommand: ApiRolePermissionsUpdateRolePermissionRelationsPutRequest
      ) {
        await api.lumen.rolePermissions.apiRolePermissionsUpdateRolePermissionRelationsPut(
          updateRolePermissionRelationsCommand
        );
      },

      async getAllPermissions(): Promise<void> {
        const data =
          await api.lumen.rolePermissions.apiRolePermissionsGetAllPermissionsGet();

        dispatch(permissionsAndRolesAssign.setAllPermsissions(data));
      },
    };
  },
});
